import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import api from '../../config/api';
import { getApiUrl } from '../../config/environment';
import toast from 'react-hot-toast';
import {
  CpuChipIcon,
  KeyIcon,
  PlusIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  ClipboardDocumentCheckIcon,
  ShieldCheckIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
`;

const Title = styled.h2`
  color: ${theme.colors.text.primary};
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Description = styled.p`
  color: ${theme.colors.text.secondary};
  margin: 0;
  line-height: 1.5;
`;

const Card = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const CardTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TokenRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1rem;
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TokenInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const TokenName = styled.div`
  color: ${theme.colors.text.primary};
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const TokenMeta = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: 0.8rem;
  font-family: monospace;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.6rem 1rem;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  ${(props) => {
    if (props.variant === 'primary') {
      return `
        background: ${theme.colors.primary.main};
        color: white;
        &:hover { background: ${theme.colors.primary.dark}; }
      `;
    }
    if (props.variant === 'danger') {
      return `
        background: transparent;
        color: ${theme.colors.error.main};
        border: 1px solid ${theme.colors.error.main};
        &:hover { background: ${theme.colors.error.light}; }
      `;
    }
    return `
      background: transparent;
      color: ${theme.colors.text.primary};
      border: 1px solid ${theme.colors.border.primary};
      &:hover { background: ${theme.colors.background.secondary}; }
    `;
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NewTokenBanner = styled.div`
  padding: 1rem;
  background: ${theme.colors.success.light};
  border: 1px solid ${theme.colors.success.main};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: 1rem;
`;

const NewTokenLabel = styled.div`
  color: ${theme.colors.success.dark};
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
`;

const TokenDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: ${theme.colors.background.primary};
  border-radius: ${theme.borderRadius.sm};
  font-family: monospace;
  font-size: 0.85rem;
  color: ${theme.colors.text.primary};
  word-break: break-all;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.colors.text.secondary};
`;

const CodeBlock = styled.pre`
  background: ${theme.colors.background.primary};
  padding: 1rem;
  border-radius: ${theme.borderRadius.md};
  overflow-x: auto;
  font-family: monospace;
  font-size: 0.8rem;
  color: ${theme.colors.text.primary};
  margin: 0.75rem 0;
`;

const InputRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const NameInput = styled.input`
  flex: 1;
  padding: 0.6rem 0.9rem;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
  }
`;

const McpConnector = () => {
  const API_BASE = getApiUrl().replace(/\/$/, '');
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [freshToken, setFreshToken] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadTokens();
  }, []);

  const loadTokens = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/mcp-tokens');
      setTokens(res.data?.tokens || []);
    } catch (error) {
      console.error('Error loading MCP tokens:', error);
      toast.error(error.response?.data?.error || 'Failed to load connector tokens');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      setCreating(true);
      const res = await api.post('/api/mcp-tokens', { name: newName || undefined });
      setFreshToken(res.data);
      setNewName('');
      toast.success('Token created — copy it now, it won\'t be shown again');
      await loadTokens();
    } catch (error) {
      console.error('Error creating MCP token:', error);
      toast.error(error.response?.data?.error || 'Failed to create token');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (tokenId) => {
    if (!window.confirm('Revoke this token? Any connected AI client will lose access immediately.')) return;
    try {
      await api.delete(`/api/mcp-tokens/${tokenId}`);
      toast.success('Token revoked');
      if (freshToken?.id === tokenId) setFreshToken(null);
      await loadTokens();
    } catch (error) {
      console.error('Error revoking MCP token:', error);
      toast.error(error.response?.data?.error || 'Failed to revoke token');
    }
  };

  const handleCopy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const claudeConfig = freshToken
    ? `{
  "mcpServers": {
    "boatwise": {
      "command": "npx",
      "args": ["-y", "mcp-remote", "${API_BASE}/api/mcp"],
      "env": {
        "MCP_REMOTE_AUTHORIZATION": "Bearer ${freshToken.token}"
      }
    }
  }
}`
    : `{
  "mcpServers": {
    "boatwise": {
      "command": "npx",
      "args": ["-y", "mcp-remote", "${API_BASE}/api/mcp"],
      "env": {
        "MCP_REMOTE_AUTHORIZATION": "Bearer YOUR_TOKEN_HERE"
      }
    }
  }
}`;

  return (
    <Container>
      <Header>
        <Title>
          <CpuChipIcon className="w-6 h-6" />
          AI Connector (MCP)
        </Title>
        <Description>
          Connect Claude Desktop or ChatGPT directly to your BoatWise account using the Model
          Context Protocol (MCP). Read-only — no boat network access required, no device control.
          Your data stays behind BoatWise Cloud; the AI client only ever talks to{' '}
          <code>{API_BASE}/api/mcp</code> using the token below.
        </Description>
      </Header>

      {freshToken && (
        <NewTokenBanner>
          <NewTokenLabel>
            <ShieldCheckIcon className="w-4 h-4 inline mr-1" />
            New token created — copy it now, it will not be shown again
          </NewTokenLabel>
          <TokenDisplay>{freshToken.token}</TokenDisplay>
          <div style={{ marginTop: '0.75rem' }}>
            <Button variant="primary" onClick={() => handleCopy(freshToken.token)}>
              {copied ? (
                <>
                  <ClipboardDocumentCheckIcon className="w-4 h-4" /> Copied!
                </>
              ) : (
                <>
                  <DocumentDuplicateIcon className="w-4 h-4" /> Copy Token
                </>
              )}
            </Button>
          </div>
        </NewTokenBanner>
      )}

      <Card>
        <CardTitle>
          <KeyIcon className="w-5 h-5" />
          Connector Tokens
        </CardTitle>

        <InputRow>
          <NameInput
            placeholder="Token name (e.g. Claude Desktop - MacBook)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <Button variant="primary" onClick={handleCreate} disabled={creating}>
            <PlusIcon className="w-4 h-4" />
            {creating ? 'Creating…' : 'New Token'}
          </Button>
        </InputRow>

        {loading ? (
          <EmptyState>Loading…</EmptyState>
        ) : tokens.length === 0 ? (
          <EmptyState>No connector tokens yet. Create one above to get started.</EmptyState>
        ) : (
          tokens.map((t) => (
            <TokenRow key={t.id}>
              <TokenInfo>
                <TokenName>
                  {t.name} {!t.is_active && <span style={{ color: theme.colors.error.main, fontWeight: 400 }}>(revoked)</span>}
                </TokenName>
                <TokenMeta>
                  <span>{t.masked_token}</span>
                  <span>
                    <ClockIcon className="w-3 h-3 inline" />{' '}
                    {t.last_used_at ? `used ${new Date(t.last_used_at).toLocaleString()}` : 'never used'}
                  </span>
                  <span>{t.call_count || 0} calls</span>
                </TokenMeta>
              </TokenInfo>
              {t.is_active && (
                <Button variant="danger" onClick={() => handleRevoke(t.id)}>
                  <TrashIcon className="w-4 h-4" />
                  Revoke
                </Button>
              )}
            </TokenRow>
          ))
        )}
      </Card>

      <Card>
        <CardTitle>
          <ShieldCheckIcon className="w-5 h-5" />
          Connect Claude Desktop
        </CardTitle>
        <Description style={{ marginBottom: '0.75rem' }}>
          Create a token above, then add this to Claude Desktop → Settings → Developer → Edit Config
          (<code>claude_desktop_config.json</code>). This uses the <code>mcp-remote</code> bridge since
          BoatWise's MCP server is hosted in the cloud, not on your machine.
        </Description>
        <CodeBlock>{claudeConfig}</CodeBlock>
        <Description style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
          Restart Claude Desktop after saving. Then try asking: "What's my boat's status?" or "Are there
          any active alarms on my boat?"
        </Description>
      </Card>

      <Card>
        <CardTitle>
          <ShieldCheckIcon className="w-5 h-5" />
          What the AI can (and can't) see
        </CardTitle>
        <Description>
          Read-only: vessel list, live SignalK data, available data paths, active alarms, trip history,
          and OnGuard status — scoped to vessels on this account only. It cannot control any device,
          change settings, or see other users' data. Revoke a token any time to cut off access
          immediately.
        </Description>
      </Card>
    </Container>
  );
};

export default McpConnector;
